import React, { useState } from "react";
import { TextField, Button, Box, Chip, Stack } from "@mui/material";
import { t } from "i18next";
import { IRole } from "@backend/model/role";

/**
 * A React component to manage a list of emails.
 * Allows adding emails via a text input and button,
 * and removing emails by clicking the 'x' on each email chip.
 */

interface IEmailListManagerProps {
  notify: boolean;
  emails: string[];
  manager?: IRole;
  users?: IRole[];
  setEmails: ( emails: string[] ) => void;
}


export const EmailListManager = ( props: IEmailListManagerProps ) => {
  // State for the current value in the input field.
  const [inputValue, setInputValue] = useState("");

  // --- Handlers ---

  /**
   * Updates the inputValue state as the user types in the TextField.
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  /**
   * Adds the email from the input field to the list.
   * It performs checks to ensure the email is not empty and not a duplicate.
   */
  const handleAddEmail = () => {
    const newEmail = props.manager?.id || "";
    // Add email only if it's not empty and not already in the list
    if (newEmail && !props.emails.includes(newEmail)) {
      props.setEmails([...props.emails, newEmail]);
      setInputValue(""); // Clear the input field after adding
    }
  };


  /**
   * Removes a specific email from the list.
   * This function is called by the Chip's onDelete handler.
   * @param {string} emailToDelete - The email to be removed.
   */
  const handleDeleteEmail = (emailToDelete: string) => {
    props.setEmails(props.emails.filter((email) => email !== emailToDelete));
  };

  // --- Render ---

  return (
    <Box
      sx={{
        p: 3,
        border: "1px solid #ddd",
        borderRadius: 2,
        maxWidth: 600,
        fontFamily: "sans-serif",
      }}
    >
      {/* Section for adding a new email */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Button
          disabled={!props.notify}
          variant="contained"
          onClick={handleAddEmail}
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("addMyEmail")}
        </Button>
      </Stack>

      {/* Section for displaying the list of emails */}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {props.emails.map((email) => (
          <Chip
            key={email}
            label={props.users?.find(e => e.id == email)?.email || ""}
            onDelete={() => handleDeleteEmail(email)}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default EmailListManager;
